import { useEffect, useRef, useState } from 'react'
import Icon from './Icon'
import { company, pipedriveForm } from '../data/site'

/**
 * Pipedrive's embedded web form.
 *
 * The enquiry goes straight into Pipedrive: the form is built and styled over
 * there, and this component only mounts it. Two consequences worth knowing,
 * because they are not visible in this file:
 *
 *   - The form is an **iframe from webforms.pipedrive.com**. It cannot inherit
 *     the site's fonts, colours or dark mode, and it is the one part of the page
 *     whose markup is not in this repository.
 *   - It is a **third-party request that sets cookies**, so the site needs a
 *     consent banner and the Datenschutzerklärung has to name Pipedrive. See the
 *     pre-launch checklist in README.md. Everything else on the site is still
 *     first-party - the fonts stay self-hosted.
 *
 * ---------------------------------------------------------------------------
 * Why the script is (re-)inserted here rather than sitting in index.html
 * ---------------------------------------------------------------------------
 * Pipedrive's loader scans for `.pipedriveWebForms` once per execution:
 *
 *     var scan = function () {
 *       document.querySelectorAll('.pipedriveWebForms').forEach(function (node) {
 *         node.getAttribute('data-pd-webforms') && !node.id && mount(node)
 *       })
 *     }
 *     'interactive' === document.readyState || 'complete' === document.readyState
 *       ? scan()
 *       : window.addEventListener('DOMContentLoaded', scan)
 *
 * In a single-page app the document is long past `DOMContentLoaded` by the time
 * anyone navigates to /kontakt, so a loader in index.html would have run before
 * the container existed and never run again - the form would appear on a full
 * page load and stay blank on every client-side navigation. Appending a fresh
 * script element on mount makes the browser execute it again, and the `!node.id`
 * check above means an already-mounted container is skipped rather than doubled.
 */

const LOADER_SRC = 'https://webforms.pipedrive.com/f/loader'

/** How long to wait before assuming the script will not arrive. */
const TIMEOUT_MS = 10_000

export default function PipedriveForm({ formUrl = pipedriveForm.url }) {
  const hostRef = useRef(null)
  const [status, setStatus] = useState('loading') // 'loading' | 'ready' | 'blocked'

  useEffect(() => {
    const host = hostRef.current
    if (!host) return undefined

    setStatus('loading')

    // The container the loader looks for. Created here rather than rendered by
    // React so React never diffs a subtree that a third-party script owns.
    const mount = document.createElement('div')
    mount.className = 'pipedriveWebForms'
    mount.setAttribute('data-pd-webforms', formUrl)
    host.replaceChildren(mount)

    const script = document.createElement('script')
    script.src = LOADER_SRC
    script.async = true
    // Content blockers are common enough in Germany that a silent blank space is
    // the likely outcome, not the exception - so treat it as a real state.
    script.addEventListener('error', () => setStatus('blocked'))
    document.body.appendChild(script)

    // The loader replaces the container's contents with an iframe. That is the
    // only reliable signal that it worked; there is no callback.
    const observer = new MutationObserver(() => {
      if (host.querySelector('iframe')) {
        setStatus('ready')
        observer.disconnect()
      }
    })
    observer.observe(host, { childList: true, subtree: true })

    const timer = setTimeout(() => {
      setStatus((current) => (current === 'loading' ? 'blocked' : current))
    }, TIMEOUT_MS)

    return () => {
      clearTimeout(timer)
      observer.disconnect()
      script.remove()
      host.replaceChildren()
    }
  }, [formUrl])

  return (
    <div className="pd-form">
      <div
        className={`pd-form__host pd-form__host--${status}`}
        ref={hostRef}
        // The iframe carries its own accessible name from Pipedrive.
        aria-busy={status === 'loading'}
      />

      {status === 'loading' ? (
        <p className="pd-form__status" role="status">
          Formular wird geladen ...
        </p>
      ) : null}

      {status === 'blocked' ? (
        <p className="pd-form__status pd-form__status--error" role="alert">
          <Icon name="mail" />
          <span>
            Das Formular konnte nicht geladen werden - möglicherweise blockiert
            es ein Browser-Add-on. Schreiben Sie uns gern direkt an{' '}
            <a href={company.emailHref}>{company.email}</a> oder rufen Sie uns
            an: <a href={company.phoneHref}>{company.phone}</a>.
          </span>
        </p>
      ) : null}
    </div>
  )
}
